<script setup lang="ts">
const props = defineProps<{
  value: boolean;
  disabled?: boolean;
  loading?: boolean;
  indeterminate?: boolean;
}>();

// `indeterminate` is an IDL property of the checkbox, not a reflectable HTML
// attribute, so it cannot be set declaratively in the template — sync it to
// the element via a ref.
const inputRef = ref<HTMLInputElement | null>(null);

watchEffect(() => {
  if (inputRef.value) {
    inputRef.value.indeterminate = !!props.indeterminate;
  }
});
</script>

<template>
  <input
    ref="inputRef"
    type="checkbox"
    :checked="value"
    :disabled="disabled || loading"
    :class="{ 'is-loading': loading, disabled: disabled }"
  />
</template>

<style lang="scss" scoped>
input[type='checkbox'] {
  border: 1px solid var(--color-gray-400);
  pointer-events: none;
  cursor: pointer;
}

input[type='checkbox']:checked {
  border: 1px solid var(--color-primary-700);
  background-color: var(--color-primary-50);
  animation: checkbox-animation 0.4s;
}

input[type='checkbox']:checked:after {
  content: '\2713';
  color: var(--color-primary-500);
  font-weight: var(--font-weight-bold);
  animation: scale-up-center 0.5s;

  opacity: 1;
}

input[type='checkbox']:indeterminate {
  border: 1px solid var(--color-primary-700);
  background-color: var(--color-primary-50);
}

input[type='checkbox']:indeterminate:after {
  content: '\2013';
  color: var(--color-primary-500);
  font-weight: var(--font-weight-bold);
  opacity: 1;
}

input[type='checkbox'] {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px !important;
  height: 20px !important;
  appearance: none;
  border-radius: var(--border-radius-4);
  box-shadow: none;
  font-size: 1em;
  margin-right: var(--space-8);
  flex-shrink: 0;
}

input[type='checkbox'].disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
